import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Alert,
  Typography,
  Button,
  Steps,
  Table,
  Checkbox,
  Progress,
  Statistic,
  Row,
  Col,
  Switch,
  Divider,
  message,
  Space,
  Tag,
  theme,
} from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import { PayrollPeriod } from "../../../types/hr";

const { Title, Text } = Typography;
const { Step } = Steps;

interface CalculationLaunchModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  onCancel: () => void;
  onSuccess: () => void;
}

interface Employee {
  id: number;
  employee_id?: number;
  employee_number: string;
  first_name: string;
  last_name: string;
  professional_category: string;
  dependents_count: number;
  has_variables: boolean;
  is_calculated: boolean;
  calculation_status?: string;
  validated_by_user?: number;
}

const CalculationLaunchModal: React.FC<CalculationLaunchModalProps> = ({
  visible,
  period,
  onCancel,
  onSuccess,
}) => {
  const { token } = theme.useToken();
  const requestVersion = useRef(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [forceRecalculation, setForceRecalculation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [calculationResults, setCalculationResults] = useState<any>(null);
  const [calculationError, setCalculationError] = useState<string | null>(null);

  // Charger les employés de la période
  useEffect(() => {
    if (visible && period) {
      fetchEmployees();
    }
    return () => { requestVersion.current++; };
  }, [visible, period?.id, period?.company_id]);

  const fetchEmployees = async () => {
    const version = ++requestVersion.current;
    setCurrentStep(0); setForceRecalculation(false); setCalculating(false); setCalculationResults(null); setCalculationProgress(0);
    setEmployees([]); setSelectedEmployees([]); setCalculationError(null);
    setLoading(true);
    try {
      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period?.id}/employees`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
            ...(period?.company_id ? { "X-Company-Id": String(period.company_id) } : {}),
          },
        }
      );
      const data = await response.json();

      if (requestVersion.current !== version) return;
      if (response.ok && data.success) {
        // Older responses use id for the period membership rather than the employee.
        const periodEmployees: Employee[] = (data.data || []).map((emp: Employee) => ({
          ...emp,
          id: emp.employee_id ?? emp.id,
        }));
        setEmployees(periodEmployees);
        // Sélectionner par défaut les employés qui ont des variables mais ne sont pas calculés
        const defaultSelected =
          periodEmployees
            .filter((emp) => emp.has_variables && !emp.is_calculated && emp.calculation_status !== "validated" && Number(emp.validated_by_user || 0) !== 1)
            .map((emp) => emp.id);
        setSelectedEmployees(defaultSelected);
      } else throw new Error(data.error || "Impossible de charger les employés");
    } catch (error) {
      console.error("Erreur lors du chargement des employés:", error);
      if (requestVersion.current === version) setCalculationError("Les employés n’ont pas pu être chargés. Actualisez avant de calculer.");
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  };

  const handleEmployeeSelection = (employeeId: number, checked: boolean) => {
    if (checked) {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    } else {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const eligibleEmployees = employees
        .filter((emp) => emp.calculation_status !== "validated" && Number(emp.validated_by_user || 0) !== 1 && emp.has_variables)
        .map((emp) => emp.id);
      setSelectedEmployees(eligibleEmployees);
    } else {
      setSelectedEmployees([]);
    }
  };

  const startCalculation = async () => {
    if (selectedEmployees.length === 0) {
      message.warning("Aucun employé sélectionné pour le calcul !");
      return;
    }
    setCalculating(true);
    setCalculationProgress(0);
    setCalculationResults(null);
    setCalculationError(null);
    const version = requestVersion.current;

    try {
      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period?.id}/calculations/launch`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
            ...(period?.company_id ? { "X-Company-Id": String(period.company_id) } : {}),
          },
          body: JSON.stringify({
            employee_ids: selectedEmployees,
            force_recalculate: forceRecalculation,
          }),
        }
      );

      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(
          `Réponse non-JSON (status ${response.status}): ${text.substring(
            0,
            200
          )}`
        );
      }

      if (version !== requestVersion.current) return;
      if (response.ok && (data.success || data.data?.errors > 0)) {
        setCalculationProgress(100);
        setCalculationResults(data.data);
        setCurrentStep(2);
        if (data.data?.errors > 0 || data.data?.pending_errors > 0) {
          message.warning("Certains calculs ont échoué. Consultez le résultat avant de poursuivre.");
        } else if (Number(data.data?.pending_employees || 0) > 0) {
          message.warning("Les calculs sélectionnés sont réalisés. La période comporte encore des salariés à calculer.");
        } else {
          message.success(data.message || "Calculs terminés avec succès");
        }
      } else {
        throw new Error(data.error || data.message || "Erreur lors du calcul");
      }
    } catch (error) {
      if (version !== requestVersion.current) return;
      console.error("Erreur lors du calcul:", error);
      const errorMessage = error instanceof Error ? error.message : "Erreur lors du calcul de la paie";
      setCalculationError(errorMessage);
      setCurrentStep(0);
      message.error(errorMessage);
    } finally {
      if (version === requestVersion.current) setCalculating(false);
    }
  };

  const resetModal = () => {
    requestVersion.current++;
    setCurrentStep(0);
    setSelectedEmployees([]);
    setForceRecalculation(false);
    setCalculationProgress(0);
    setCalculationResults(null);
    setCalculationError(null);
    setCalculating(false);
  };

  const handleCancel = () => {
    resetModal();
    onCancel();
  };

  const handleSuccess = () => {
    resetModal();
    onSuccess();
  };

  const employeeColumns = [
    {
      title: "Sélection",
      dataIndex: "id",
      width: 80,
      render: (id: number, record: Employee) => {
        const isEligible = record.calculation_status !== "validated" && Number(record.validated_by_user || 0) !== 1 && record.has_variables;
        const isSelected = selectedEmployees.includes(id);

        return (
          <Checkbox
            checked={isSelected}
            disabled={!isEligible}
            onChange={(e) => handleEmployeeSelection(id, e.target.checked)}
          />
        );
      },
    },
    {
      title: "N° Employé",
      dataIndex: "employee_number",
      width: 120,
    },
    {
      title: "Nom Complet",
      render: (record: Employee) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: "Catégorie",
      dataIndex: "professional_category",
      width: 100,
      render: (category: string) => (
        <Tag color={category === "cadre" ? "blue" : "green"}>
          {category === "cadre" ? "Cadre" : "Non-cadre"}
        </Tag>
      ),
    },
    {
      title: "Charges",
      dataIndex: "dependents_count",
      width: 80,
      align: "center" as const,
    },
    {
      title: "Variables",
      dataIndex: "has_variables",
      width: 100,
      render: (hasVariables: boolean) => (
        <Tag color={hasVariables ? "green" : "orange"}>
          {hasVariables ? "Saisies" : "Manquantes"}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_calculated",
      width: 100,
      render: (isCalculated: boolean) => (
        <Tag color={isCalculated ? "blue" : "default"}>
          {isCalculated ? "Calculé" : "En attente"}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: "Sélection",
      description: "Choisir les employés",
      icon: <TeamOutlined />,
    },
    {
      title: "Calcul",
      description: calculating ? "Traitement en cours" : "Lancer le traitement",
      icon: <CalculatorOutlined />,
    },
    {
      title: "Résultats",
      description: "Calculs terminés",
      icon: <CheckCircleOutlined />,
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            {calculationError && (
              <Alert
                message="Erreur lors du calcul"
                description={calculationError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <Statistic
                  title="Total employés"
                  value={employees.length}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Variables saisies"
                  value={employees.filter((emp) => emp.has_variables).length}
                  prefix={<CheckCircleOutlined style={{ color: token.colorSuccess }} />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Sélectionnés"
                  value={selectedEmployees.length}
                  prefix={<PlayCircleOutlined style={{ color: token.colorPrimary }} />}
                />
              </Col>
            </Row>

            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 16 }}
            >
              <Col>
                <Space>
                  <Text strong>Options de calcul :</Text>
                  <Switch
                    checked={forceRecalculation}
                    onChange={setForceRecalculation}
                    checkedChildren="Recalcul forcé"
                    unCheckedChildren="Nouveau calcul"
                  />
                </Space>
              </Col>
              <Col>
                <Checkbox
                  disabled={!employees.some(emp => emp.has_variables && emp.calculation_status !== 'validated' && Number(emp.validated_by_user || 0) !== 1)}
                  indeterminate={
                    selectedEmployees.length > 0 &&
                    selectedEmployees.length <
                      employees.filter(
                        (emp) => emp.has_variables
                      ).length
                  }
                  checked={
                    selectedEmployees.length > 0 &&
                    selectedEmployees.length ===
                    employees.filter(
                      (emp) => emp.has_variables && emp.calculation_status !== 'validated' && Number(emp.validated_by_user || 0) !== 1
                    ).length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Sélectionner tout
                </Checkbox>
              </Col>
            </Row>

            <Table
              dataSource={employees}
              columns={employeeColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ y: 300 }}
              loading={loading}
            />

            <Divider />

            <Alert
              message="Calculs selon la législation Burkina Faso"
              description="Les calculs utiliseront les tranches IUTS, exonérations et abattements forfaitaires selon le statut professionnel et le nombre de charges."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </div>
        );

      case 1:
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Title level={3}>{calculating ? "Calcul de la paie en cours..." : "Prêt à lancer le calcul de la paie"}</Title>
            <Progress
              type="circle"
              percent={calculationProgress}
              status={calculating ? "active" : "normal"}
              size={120}
            />
            <div style={{ marginTop: 20 }}>
              <Text type="secondary">
                Traitement de {selectedEmployees.length} employé(s) selon la
                législation Burkina Faso
              </Text>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Alert
              message={calculationResults?.errors > 0 || calculationResults?.pending_errors > 0 ? "Calculs terminés avec des erreurs" : Number(calculationResults?.pending_employees || 0) > 0 ? "Calculs réalisés, période à compléter" : "Calculs terminés"}
              description={
                <div>
                  <div>{calculationResults?.successful || 0} calcul(s) effectué(s), {calculationResults?.skipped || 0} déjà présent(s), {calculationResults?.errors || 0} erreur(s).</div>
                  {Number(calculationResults?.pending_employees || 0) > 0 && <div>{calculationResults.pending_employees} salarié(s) de la période reste(nt) à calculer.</div>}
                  {calculationResults?.preserved > 0 && (
                    <div>{calculationResults.preserved} calcul(s) précédent(s) conservé(s).</div>
                  )}
                  {calculationResults?.pending_errors > (calculationResults?.errors || 0) && (
                    <div>{calculationResults.pending_errors - (calculationResults.errors || 0)} autre(s) calcul(s) de la période reste(nt) à reprendre.</div>
                  )}
                  {calculationResults?.error_details?.length > 0 && (
                    <ul>{calculationResults.error_details.map((detail: string, index: number) => <li key={index}>{detail}</li>)}</ul>
                  )}
                </div>
              }
              type={calculationResults?.errors > 0 || calculationResults?.pending_errors > 0 ? (calculationResults?.successful > 0 ? "warning" : "error") : Number(calculationResults?.pending_employees || 0) > 0 ? "warning" : "success"}
              showIcon
              style={{ marginBottom: 20 }}
            />

            {calculationResults && (
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Calculs réussis"
                    value={calculationResults.successful || 0}
                    valueStyle={{ color: token.colorSuccess }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Calculs en erreur"
                    value={calculationResults.errors || 0}
                    valueStyle={{ color: token.colorError }}
                    prefix={<ExclamationCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Masse salariale"
                    value={calculationResults.total_net || 0}
                    formatter={(value) =>
                      `${Number(value).toLocaleString()} FCFA`
                    }
                    valueStyle={{ color: token.colorInfo }}
                  />
                </Col>
              </Row>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const footer = () => {
    switch (currentStep) {
      case 0:
        return [
          <Button key="cancel" onClick={handleCancel}>
            Annuler
          </Button>,
          <Button
            key="launch"
            type="primary"
            icon={<PlayCircleOutlined />}
            disabled={selectedEmployees.length === 0}
            onClick={() => setCurrentStep(1)}
          >
            Lancer les calculs ({selectedEmployees.length})
          </Button>,
        ];

      case 1:
        return [
          <Button
            key="start"
            type="primary"
            loading={calculating}
            onClick={startCalculation}
            disabled={calculating}
          >
            {calculating ? "Calcul en cours..." : "Démarrer"}
          </Button>,
        ];

      case 2:
        return [
          <Button
            key="recalculate"
            icon={<ReloadOutlined />}
            onClick={() => setCurrentStep(0)}
          >
            Nouveau calcul
          </Button>,
          <Button key="finish" type="primary" onClick={handleSuccess}>
            Terminer
          </Button>,
        ];

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <span>
          <PlayCircleOutlined style={{ marginRight: 8 }} />
          Lancement des Calculs IUTS - {period?.name}
        </span>
      }
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={footer()}
      destroyOnClose
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {renderStepContent()}
    </Modal>
  );
};

export default CalculationLaunchModal;
